import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Bell, Gift, MessageSquare, User, Calendar, ArrowLeft, Settings, Search } from 'lucide-react';
import { contractService } from '../../services/contractService';
import { getTemplates, MessageTemplate } from '../../services/messageTemplateService';
import { Contract } from '../../types/contract';
import { PageTransition } from '../ui/PageTransition';
import { PageTitle } from '../ui/PageTitle';

interface AniversarioLembrete {
  id: string;
  contratante: string;
  telefone: string;
  nomeAniversariante: string;
  novaIdade: number;
  proximoAniversario: Date;
  diasRestantes: number;
}

const LEMBRETES_PER_PAGE = 8;

const Lembretes: React.FC = () => {
  const navigate = useNavigate();
  const [allLembretes, setAllLembretes] = useState<AniversarioLembrete[]>([]);
  const [displayedLembretes, setDisplayedLembretes] = useState<AniversarioLembrete[]>([]);
  const [templates, setTemplates] = useState<MessageTemplate[]>([]);
  const [selectedTemplateId, setSelectedTemplateId] = useState<string>('');
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [isSticky, setIsSticky] = useState(false);

  const filterAndLoadLembretes = useCallback(() => {
    const filtered = allLembretes.filter(lembrete =>
      lembrete.contratante.toLowerCase().includes(searchTerm.toLowerCase()) ||
      lembrete.nomeAniversariante.toLowerCase().includes(searchTerm.toLowerCase())
    );

    setPage(1);
    const initialLembretes = filtered.slice(0, LEMBRETES_PER_PAGE);
    setDisplayedLembretes(initialLembretes);
    setHasMore(initialLembretes.length < filtered.length);
  }, [allLembretes, searchTerm]);

  useEffect(() => {
    filterAndLoadLembretes();
  }, [searchTerm, allLembretes, filterAndLoadLembretes]);

  const loadMoreLembretes = useCallback(() => {
    if (!hasMore || loading) return;

    const filtered = allLembretes.filter(lembrete =>
      lembrete.contratante.toLowerCase().includes(searchTerm.toLowerCase()) ||
      lembrete.nomeAniversariante.toLowerCase().includes(searchTerm.toLowerCase())
    );

    const nextPage = page + 1;
    const nextLembretes = filtered.slice(0, nextPage * LEMBRETES_PER_PAGE);
    
    setPage(nextPage);
    setDisplayedLembretes(nextLembretes);
    setHasMore(nextLembretes.length < filtered.length);
  }, [page, hasMore, loading, allLembretes, searchTerm]);

  useEffect(() => {
    const handleScroll = () => {
      // Verifica se já está carregando ou se não há mais itens
      if (loading || !hasMore) return;

      // Condição para carregar mais itens quando o usuário estiver a 200px do final da página
      if (window.innerHeight + document.documentElement.scrollTop >= document.documentElement.scrollHeight - 200) {
        loadMoreLembretes();
      }
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [loading, hasMore, loadMoreLembretes]);

  useEffect(() => {
    const handleStickyScroll = () => {
      // Detecta quando o usuário rola mais de 100px para ativar o modo sticky
      const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
      const shouldBeSticky = scrollTop > 100;
      console.log('Scroll:', scrollTop, 'Sticky:', shouldBeSticky);
      setIsSticky(shouldBeSticky);
    };

    window.addEventListener('scroll', handleStickyScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleStickyScroll);
  }, []);

  useEffect(() => {
    const loadData = async () => {
      try {
        setLoading(true);
        const [todosContratos, fetchedTemplates] = await Promise.all([
          contractService.getContracts(),
          getTemplates()
        ]);
        
        setTemplates(fetchedTemplates);
        if (fetchedTemplates.length > 0) {
          setSelectedTemplateId(fetchedTemplates[0].id);
        }

        const contratosConcluidos = todosContratos.filter(c => c.status === 'concluido' && c.dataFesta);
        const hoje = new Date();
        hoje.setHours(0, 0, 0, 0);

        const lembretesPotenciais: AniversarioLembrete[] = [];
        contratosConcluidos.forEach(contrato => {
          const dataFestaAnterior = new Date(contrato.dataFesta);
          const proximoAniversario = new Date(dataFestaAnterior);
          proximoAniversario.setFullYear(hoje.getFullYear());

          if (proximoAniversario < hoje) {
            proximoAniversario.setFullYear(hoje.getFullYear() + 1);
          }

          const umDiaEmMs = 1000 * 60 * 60 * 24;
          const diasRestantes = Math.round((proximoAniversario.getTime() - hoje.getTime()) / umDiaEmMs);

          // Inclui todos os aniversários futuros
          if (diasRestantes >= 0) {
            lembretesPotenciais.push({
              id: contrato.id!,
              contratante: contrato.contratante,
              telefone: contrato.telefone,
              nomeAniversariante: contrato.nomeAniversariante,
              novaIdade: (parseInt(String(contrato.idadeAniversariante) || '', 10) || 0) + (proximoAniversario.getFullYear() - dataFestaAnterior.getFullYear()),
              proximoAniversario,
              diasRestantes,
            });
          }
        });

        lembretesPotenciais.sort((a, b) => a.diasRestantes - b.diasRestantes);
        setAllLembretes(lembretesPotenciais);

      } catch (error) {
        console.error("Erro ao gerar lembretes:", error);
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, []);

  const handleSendWhatsApp = (lembrete: AniversarioLembrete) => {
    const selectedTemplate = templates.find(t => t.id === selectedTemplateId);
    if (!selectedTemplate) {
      alert("Por favor, selecione um modelo de mensagem.");
      return;
    }

    let mensagem = selectedTemplate.content;
    mensagem = mensagem.replace(/{{contratante}}/g, lembrete.contratante);
    mensagem = mensagem.replace(/{{nomeAniversariante}}/g, lembrete.nomeAniversariante);
    mensagem = mensagem.replace(/{{novaIdade}}/g, lembrete.novaIdade.toString());
    mensagem = mensagem.replace(/{{proximoAniversario}}/g, lembrete.proximoAniversario.toLocaleDateString('pt-BR'));
    mensagem = mensagem.replace(/{{diasRestantes}}/g, lembrete.diasRestantes.toString());

    const whatsappUrl = `https://wa.me/55${lembrete.telefone?.replace(/\D/g, '')}?text=${encodeURIComponent(mensagem)}`;
    window.open(whatsappUrl, '_blank');
  };

  if (loading && page === 1) {
    return (
      <PageTransition>
        <div className="flex items-center justify-center min-h-screen">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-teal-500 mx-auto mb-4"></div>
            <p className="text-slate-600">Analisando aniversários...</p>
          </div>
        </div>
      </PageTransition>
    );
  }

  return (
    <>
      {/* Campo fixo que aparece quando rola */}
      {isSticky && (
        <div className="fixed top-4 left-72 lg:left-80 right-4 z-40 p-4 bg-white/95 backdrop-blur-sm rounded-2xl shadow-2xl border border-teal-200 space-y-4 animate-[slideInFromTop_0.3s_ease-out_forwards]">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <div className="w-full md:w-1/2">
              <label htmlFor="template-select-sticky" className="block text-sm font-medium text-slate-700 mb-2">
                Selecione o Modelo de Mensagem
              </label>
              <select
                id="template-select-sticky"
                value={selectedTemplateId}
                onChange={(e) => setSelectedTemplateId(e.target.value)}
                className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500"
                disabled={templates.length === 0}
              >
                {templates.length > 0 ? (
                  templates.map(template => (
                    <option key={template.id} value={template.id}>{template.name}</option>
                  ))
                ) : (
                  <option>Nenhum modelo disponível</option>
                )}
              </select>
            </div>
            <button
              onClick={() => navigate('/admin/mensagens')}
              className="w-full md:w-auto flex items-center justify-center gap-2 px-4 py-2 bg-slate-100 text-slate-700 rounded-lg hover:bg-slate-200 transition-colors"
            >
              <Settings className="w-4 h-4" />
              Gerenciar Modelos
            </button>
          </div>
          <div className="relative w-full">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400 w-5 h-5" />
            <input
              type="text"
              placeholder="Buscar por contratante ou aniversariante..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500"
            />
          </div>
        </div>
      )}

      <PageTransition>
        <div className="w-full mx-auto px-2 sm:px-4 lg:px-6 overflow-x-hidden">
          <div className="mx-2 sm:mx-4 lg:mx-6 mt-2 mb-3">
            <PageTitle
              title="Lembretes de Aniversário"
              subtitle="Reconecte-se com clientes e incentive novas festas!"
            >
              <button
                onClick={() => navigate(`/admin/dashboard`)}
                className="px-4 py-2.5 bg-white/20 backdrop-blur-sm text-white rounded-xl hover:bg-white/30 flex items-center gap-2 font-semibold border border-white/30 transition-all duration-200"
              >
                <ArrowLeft className="w-4 h-4" />
                Voltar ao Dashboard
              </button>
            </PageTitle>
          </div>

          <div className="p-4">
            <div className="mb-6 p-4 bg-white/80 backdrop-blur-sm rounded-2xl shadow-lg border border-slate-200/50 space-y-4">
              <div className="flex flex-col md:flex-row items-center justify-between gap-4">
                <div className="w-full md:w-1/2">
                  <label htmlFor="template-select" className="block text-sm font-medium text-slate-700 mb-2">
                    Selecione o Modelo de Mensagem
                  </label>
                  <select
                    id="template-select"
                    value={selectedTemplateId}
                    onChange={(e) => setSelectedTemplateId(e.target.value)}
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500"
                    disabled={templates.length === 0}
                  >
                    {templates.length > 0 ? (
                      templates.map(template => (
                        <option key={template.id} value={template.id}>{template.name}</option>
                      ))
                    ) : (
                      <option>Nenhum modelo disponível</option>
                    )}
                  </select>
                </div>
                <button
                  onClick={() => navigate('/admin/mensagens')}
                  className="w-full md:w-auto flex items-center justify-center gap-2 px-4 py-2 bg-slate-100 text-slate-700 rounded-lg hover:bg-slate-200 transition-colors"
                >
                  <Settings className="w-4 h-4" />
                  Gerenciar Modelos
                </button>
              </div>
              <div className="relative w-full">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400 w-5 h-5" />
                <input
                  type="text"
                  placeholder="Buscar por contratante ou aniversariante..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500"
                />
              </div>
            </div>

          {displayedLembretes.length === 0 && !loading ? (
            <div className={`text-center py-16 bg-white/80 backdrop-blur-sm rounded-2xl shadow-xl border border-slate-200/50 ${isSticky ? 'mt-40' : ''}`}>
              <Gift className="w-16 h-16 mx-auto text-teal-400 mb-4" />
              <h3 className="text-xl font-semibold text-slate-800">Nenhum aniversário encontrado!</h3>
              <p className="text-slate-500 mt-2">
                Nenhum lembrete corresponde à sua busca ou não há aniversários nos próximos 60 dias.
              </p>
            </div>
          ) : (
            <>
              <div className={`grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6 ${isSticky ? 'mt-40' : ''}`}>
                {displayedLembretes.map(lembrete => (
                  <div key={lembrete.id} className="bg-white/80 backdrop-blur-sm rounded-2xl shadow-xl border border-slate-200/50 overflow-hidden flex flex-col justify-between transform hover:-translate-y-1 transition-transform duration-300">
                    <div className="p-5">
                      <div className="flex items-center gap-4 mb-4">
                        <div className="h-12 w-12 bg-gradient-to-br from-teal-400 to-cyan-500 rounded-xl flex items-center justify-center text-white shadow-lg">
                          <Gift className="w-6 h-6" />
                        </div>
                        <div>
                          <h3 className="font-bold text-slate-900 text-lg">{lembrete.nomeAniversariante}</h3>
                          <p className="text-sm text-slate-600">Fará {lembrete.novaIdade} anos</p>
                        </div>
                      </div>
                      <div className="space-y-2 text-sm">
                        <div className="flex items-center gap-2 text-slate-700">
                          <User className="w-4 h-4 text-slate-400" />
                          <span>Responsável: {lembrete.contratante}</span>
                        </div>
                        <div className="flex items-center gap-2 text-slate-700">
                          <Calendar className="w-4 h-4 text-slate-400" />
                          <span>
                            Aniversário em: {lembrete.proximoAniversario.toLocaleDateString('pt-BR')}
                          </span>
                        </div>
                      </div>
                    </div>
                    <div className="bg-teal-50 p-4 text-center">
                      <p className="font-bold text-teal-800 text-lg">
                        Faltam {lembrete.diasRestantes} dias!
                      </p>
                    </div>
                    <div className="p-4 bg-slate-50/50">
                      <button
                        onClick={() => handleSendWhatsApp(lembrete)}
                        disabled={!selectedTemplateId}
                        className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-green-600 text-white rounded-xl shadow-lg hover:shadow-xl hover:bg-green-700 transform hover:scale-105 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        <MessageSquare className="w-5 h-5" />
                        <span className="font-semibold">Enviar Lembrete</span>
                      </button>
                    </div>
                  </div>
                ))}
              </div>
              {hasMore && (
                <div className="text-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-teal-500 mx-auto"></div>
                  <p className="text-slate-500 mt-2 text-sm">Carregando mais...</p>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </PageTransition>
    </>
  );
};

export default Lembretes;